import React from "react"
import { createPortal } from "react-dom"
import Spinner from "react-bootstrap/Spinner"
import { Check, Pencil, RotateCcw } from "lucide-react"
import EditActionIconButton from "./EditActionIconButton.jsx"

const MIN_SPINNER_MS = 300

/**
 * @param {{
 *   value?: string | number | null,
 *   displayValue?: string,
 *   options?: Array<{ value: string | number, label: string }>,
 *   onSave?: (nextValue: string) => Promise<void> | void,
 *   onRevert?: () => Promise<void> | void,
 *   onError?: (error: unknown) => void,
 *   disabled?: boolean,
 *   width?: string,
 *   showRevertButton?: boolean,
 * }} props
 */
export default function EditableDropdown({
    value = "",
    displayValue = "—",
    options = [],
    onSave = async (_nextValue) => {},
    onRevert = async () => {},
    onError = (_error) => {},
    disabled = false,
    width = "12rem",
    showRevertButton = true,
}) {
    const containerRef = React.useRef(/** @type {HTMLDivElement | null} */ (null))
    const [isEditing, setIsEditing] = React.useState(false)
    const [isSaving, setIsSaving] = React.useState(false)
    const [isReverting, setIsReverting] = React.useState(false)
    const [draftValue, setDraftValue] = React.useState(String(value ?? ""))
    const [iconState, setIconState] = React.useState(
        /** @type {"idle" | "saving" | "success" | "error"} */ ("idle")
    )
    const iconResetTimerRef = React.useRef(/** @type {number | null} */ (null))
    const [overlayPosition, setOverlayPosition] = React.useState({
        top: 0,
        left: 0,
        dropdownWidth: 200,
    })

    React.useEffect(() => {
        if (!isEditing && !isSaving && !isReverting) {
            setDraftValue(String(value ?? ""))
        }
    }, [value, isEditing, isSaving, isReverting])

    React.useEffect(() => () => {
        if (iconResetTimerRef.current != null) {
            window.clearTimeout(iconResetTimerRef.current)
        }
    }, [])

    React.useEffect(() => {
        if (!isEditing) {
            return
        }

        function parseWidthPx() {
            if (typeof width === "string" && width.trim().endsWith("px")) {
                const parsed = Number(width.replace("px", ""))
                if (Number.isFinite(parsed) && parsed > 0) {
                    return parsed
                }
            }
            if (typeof width === "string") {
                const parsed = Number(width)
                if (Number.isFinite(parsed) && parsed > 0) {
                    return parsed
                }
            }
            const refWidth = containerRef.current?.getBoundingClientRect().width ?? 0
            return Math.max(180, Math.round(refWidth))
        }

        function syncOverlayPosition() {
            const rect = containerRef.current?.getBoundingClientRect()
            if (!rect) {
                return
            }
            setOverlayPosition({
                top: Math.round(rect.bottom + 4),
                left: Math.round(rect.left),
                dropdownWidth: parseWidthPx(),
            })
        }

        function onDocMouseDown(event) {
            const target = event.target
            if (!(target instanceof Node)) {
                return
            }
            if (containerRef.current?.contains(target)) {
                return
            }
            const dropdownEl = document.getElementById("v2-editable-dropdown-menu")
            const actionsEl = document.getElementById("v2-editable-dropdown-actions")
            if (dropdownEl?.contains(target) || actionsEl?.contains(target)) {
                return
            }
            setIsEditing(false)
            setDraftValue(String(value ?? ""))
        }

        syncOverlayPosition()
        window.addEventListener("resize", syncOverlayPosition)
        window.addEventListener("scroll", syncOverlayPosition, true)
        document.addEventListener("mousedown", onDocMouseDown)
        return () => {
            window.removeEventListener("resize", syncOverlayPosition)
            window.removeEventListener("scroll", syncOverlayPosition, true)
            document.removeEventListener("mousedown", onDocMouseDown)
        }
    }, [isEditing, value, width])

    function scheduleIconReset() {
        if (iconResetTimerRef.current != null) {
            window.clearTimeout(iconResetTimerRef.current)
        }
        iconResetTimerRef.current = window.setTimeout(() => {
            setIconState("idle")
            iconResetTimerRef.current = null
        }, 1000)
    }

    async function ensureMinSpinner(startedAtMs) {
        const elapsed = Date.now() - startedAtMs
        const remaining = MIN_SPINNER_MS - elapsed
        if (remaining > 0) {
            await new Promise((resolve) => window.setTimeout(resolve, remaining))
        }
    }

    async function handleSave() {
        if (disabled || isSaving || isReverting) {
            return
        }
        const startedAtMs = Date.now()
        setIsSaving(true)
        setIsEditing(false)
        setIconState("saving")
        try {
            await onSave(draftValue)
            await ensureMinSpinner(startedAtMs)
            setIsEditing(false)
            setIconState("success")
            scheduleIconReset()
        } catch (error) {
            await ensureMinSpinner(startedAtMs)
            onError(error)
            setIconState("error")
            scheduleIconReset()
        } finally {
            setIsSaving(false)
        }
    }

    async function handleRevert() {
        if (!showRevertButton || disabled || isSaving || isReverting) {
            return
        }
        const startedAtMs = Date.now()
        setIsReverting(true)
        setIsEditing(false)
        setIconState("saving")
        try {
            await onRevert()
            await ensureMinSpinner(startedAtMs)
            setIsEditing(false)
            setIconState("success")
            scheduleIconReset()
        } catch (error) {
            await ensureMinSpinner(startedAtMs)
            onError(error)
            setIconState("error")
            scheduleIconReset()
        } finally {
            setIsReverting(false)
        }
    }

    const showEditButton = !disabled &&
        !isSaving &&
        !isReverting &&
        iconState === "idle"

    return (
        <div
            ref={containerRef}
            className="position-relative d-inline-flex align-items-center"
            style={{ minWidth: 0, maxWidth: "100%" }}
        >
            <span className="d-inline-flex align-items-center"
                style={{ minWidth: 0, maxWidth: "100%" }}>
                <span
                    className="fs-6 fw-semibold text-dark text-truncate"
                    style={{ minWidth: 0, maxWidth: "100%" }}
                    title={displayValue || "—"}
                >
                    {displayValue || "—"}
                </span>
            </span>
            <span className="ms-1 d-inline-flex align-items-center justify-content-center"
                style={{ width: "1.5rem", height: "1.25rem", flex: "0 0 1.5rem" }}>
                {showEditButton ? (
                    <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-primary"
                        onClick={() => {
                            setDraftValue(String(value ?? ""))
                            setIsEditing((prev) => !prev)
                        }}
                    >
                        <Pencil size={16} aria-hidden />
                    </button>
                ) : iconState !== "idle" ? (
                    <EditActionIconButton
                        state={iconState}
                        disabled={disabled || isSaving || isReverting}
                        onClick={() => {
                            if (!isEditing) {
                                setDraftValue(String(value ?? ""))
                                setIsEditing(true)
                            }
                        }}
                    />
                ) : null}
            </span>

            {isEditing ? createPortal(
                <>
                    <div
                        id="v2-editable-dropdown-menu"
                        className="bg-white border shadow"
                        style={{
                            position: "fixed",
                            top: `${overlayPosition.top}px`,
                            left: `${overlayPosition.left}px`,
                            width: `${overlayPosition.dropdownWidth}px`,
                            maxHeight: "300px",
                            overflowY: "auto",
                            zIndex: 2300,
                        }}
                    >
                        {options.map((option) => (
                            <button
                                key={String(option.value)}
                                type="button"
                                className={`w-100 text-start btn btn-sm rounded-0 ${
                                    draftValue === String(option.value) ?
                                        "btn-info-subtle text-info-emphasis fw-semibold" :
                                        "btn-light"
                                }`}
                                onClick={() => setDraftValue(String(option.value))}
                            >
                                <span className="text-truncate d-block">{option.label}</span>
                            </button>
                        ))}
                    </div>
                    <div
                        id="v2-editable-dropdown-actions"
                        className="bg-white border shadow p-2 d-flex flex-column gap-2"
                        style={{
                            position: "fixed",
                            top: `${overlayPosition.top}px`,
                            left: `${overlayPosition.left + overlayPosition.dropdownWidth + 4}px`,
                            zIndex: 2301,
                        }}
                    >
                        {showRevertButton ? (
                            <button
                                type="button"
                                className="btn btn-sm btn-light border"
                                disabled={isSaving || isReverting}
                                onClick={() => {
                                    void handleRevert()
                                }}
                                title="Revert"
                            >
                                {isReverting ? (
                                    <Spinner animation="border" size="sm" />
                                ) : (
                                    <RotateCcw size={14} />
                                )}
                            </button>
                        ) : null}
                        <button
                            type="button"
                            className="btn btn-sm btn-light border text-success"
                            disabled={isSaving || isReverting}
                            onClick={() => {
                                void handleSave()
                            }}
                            title="Save"
                        >
                            {isSaving ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <Check size={14} />
                            )}
                        </button>
                    </div>
                </>,
                document.body
            ) : null}
        </div>
    )
}
